// Chakra Imports
import {
  Avatar,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Select,
  Skeleton,
  Switch,
  Text,
  useColorMode,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Separator } from "components/Separator/Separator";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { FiLock, FiEye, FiEyeOff, FiLogOut } from "react-icons/fi";
import { getSites, saveSiteConfig, getSiteConfig, useUserAuth } from '../../utils/site';
import { makeAPIRequest } from "services/surveyService";

export default function Configurator(props) {
  const { secondary, isOpen, onClose, fixed, onSwitch, ...rest } = props;
  const [isFixed, setIsFixed] = React.useState(fixed ?? true);

  const { colorMode, toggleColorMode } = useColorMode();
  const toast = useToast();

  const secondaryButtonBg = useColorModeValue("white", "transparent");
  const secondaryButtonBorder = useColorModeValue("gray.700", "white");
  const secondaryButtonColor = useColorModeValue("gray.700", "white");
  const settingsRef = React.useRef();

  const [selectedSite, setSelectedSite] = useState("");
  const [sites, setSites] = useState([]);

  // Password change state
  const [pwOpen, setPwOpen] = useState(false);
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwShowNew, setPwShowNew] = useState(false);
  const [pwShowConfirm, setPwShowConfirm] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  const { user, loading: userLoading, error: userError } = useUserAuth();

  const openPwForm = () => { setPwNew(''); setPwConfirm(''); setPwError(''); setPwOpen(true); };
  const closePwForm = () => { setPwOpen(false); setPwError(''); };

  const handleChangePassword = async () => {
    setPwError('');
    if (!pwNew || !pwConfirm) { setPwError('Completa ambos campos'); return; }
    if (pwNew.length < 6) { setPwError('Mínimo 6 caracteres'); return; }
    if (pwNew !== pwConfirm) { setPwError('Las contraseñas no coinciden'); return; }
    setPwLoading(true);
    try {
      await makeAPIRequest('/me/password', 'put', {}, { password: pwNew });
      toast({ title: 'Contraseña actualizada', status: 'success', duration: 3000, isClosable: true });
      closePwForm();
    } catch (err) {
      setPwError(err?.response?.data?.message || err.message || 'Error al actualizar contraseña');
    } finally {
      setPwLoading(false);
    }
  };

  const switchSite = (siteName) => {
    setSelectedSite(siteName);
    const filtered = sites.filter(s => s.name === siteName);
    if (filtered.length > 0) {
      saveSiteConfig(JSON.stringify(filtered[0]));
      window.location.href = '/#';
    }
  };

  useEffect(() => {
    const get_sites = getSites();
    const get_site_config = getSiteConfig();
    if (!get_sites || !get_site_config) {
      localStorage.clear();
      window.location.href = '/#/auth/signin';
    } else {
      const sitesData = JSON.parse(get_sites);
      const siteConfig = JSON.parse(get_site_config);
      if (sitesData.length > 0 && siteConfig) {
        setSites(sitesData);
        setSelectedSite(siteConfig.name);
      }
    }
  }, []);

  return (
    <Drawer
      isOpen={props.isOpen}
      onClose={props.onClose}
      placement={document.documentElement.dir === "rtl" ? "left" : "right"}
      finalFocusRef={settingsRef}
      blockScrollOnMount={false}
    >
      <DrawerContent>
        <DrawerCloseButton mt="16px" />
        <DrawerHeader pt="24px" px="24px">
          {/* Información del usuario */}
          {userLoading ? (
            <VStack spacing={3} align="start" mt="8px" mb="16px">
              <Skeleton height="40px" width="200px" />
              <Skeleton height="20px" width="150px" />
              <Skeleton height="16px" width="120px" />
            </VStack>
          ) : user ? (
            <VStack spacing={3} align="start" mt="8px" mb="16px">
              <HStack spacing={3}>
                <Avatar
                  size="md"
                  name={user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.email}
                  bg="blue.500"
                  color="white"
                />
                <VStack align="start" spacing={1}>
                  <Text fontSize="lg" fontWeight="bold" lineHeight="1.2">
                    {user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.username}
                  </Text>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {user.email}
                  </Text>
                  <Badge
                    colorScheme={user.role === 'administrador' ? 'purple' : user.role === 'profesor' ? 'teal' : user.role === 'inspector' ? 'orange' : 'blue'}
                    size="sm"
                    borderRadius="full"
                    textTransform="capitalize"
                  >
                    {user.role === 'administrador' ? 'Administrador' :
                     user.role === 'profesor' ? 'Profesor' :
                     user.role === 'inspector' ? 'Inspector' : user.role}
                  </Badge>
                </VStack>
              </HStack>
            </VStack>
          ) : (
            <VStack spacing={2} align="start" mt="8px" mb="16px">
              <Text fontSize="xl" fontWeight="bold">Bienvenido</Text>
              <Text fontSize="sm" color="gray.500">Configuración del sistema</Text>
            </VStack>
          )}
          <Separator />
        </DrawerHeader>

        <DrawerBody w="340px" ps="24px" pe="24px">
          <Flex flexDirection="column" gap={4}>

            {/* Cambiar contraseña */}
            {!pwOpen ? (
              <Button
                leftIcon={<Icon as={FiLock} />}
                variant="ghost"
                justifyContent="flex-start"
                size="sm"
                onClick={openPwForm}
                w="full"
              >
                Cambiar contraseña
              </Button>
            ) : (
              <VStack spacing={3} align="stretch" bg={useColorModeValue('gray.50', 'gray.700')} p={3} borderRadius="md">
                <Text fontSize="sm" fontWeight="600">Nueva contraseña</Text>
                <FormControl>
                  <InputGroup size="sm">
                    <Input
                      type={pwShowNew ? 'text' : 'password'}
                      value={pwNew}
                      onChange={e => setPwNew(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      autoFocus
                    />
                    <InputRightElement>
                      <IconButton size="xs" variant="ghost"
                        icon={<Icon as={pwShowNew ? FiEyeOff : FiEye} />}
                        onClick={() => setPwShowNew(v => !v)} aria-label="toggle" />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
                <FormControl>
                  <InputGroup size="sm">
                    <Input
                      type={pwShowConfirm ? 'text' : 'password'}
                      value={pwConfirm}
                      onChange={e => setPwConfirm(e.target.value)}
                      placeholder="Confirmar contraseña"
                      onKeyPress={e => { if (e.key === 'Enter') handleChangePassword(); }}
                    />
                    <InputRightElement>
                      <IconButton size="xs" variant="ghost"
                        icon={<Icon as={pwShowConfirm ? FiEyeOff : FiEye} />}
                        onClick={() => setPwShowConfirm(v => !v)} aria-label="toggle" />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
                {pwError && <Text color="red.500" fontSize="xs">{pwError}</Text>}
                <HStack>
                  <Button size="sm" colorScheme="blue" onClick={handleChangePassword} isLoading={pwLoading} flex={1}>
                    Actualizar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={closePwForm} flex={1}>
                    Cancelar
                  </Button>
                </HStack>
              </VStack>
            )}

            <Separator />

            {/* Navbar fija */}
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontSize="sm" fontWeight="600">Barra fija</Text>
              <Switch
                colorScheme="blue"
                isChecked={isFixed}
                onChange={() => {
                  const next = !isFixed;
                  setIsFixed(next);
                  if (onSwitch) onSwitch(next);
                }}
              />
            </Flex>

            {/* Dark / Light */}
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontSize="sm" fontWeight="600">
                Modo {colorMode === "light" ? "oscuro" : "claro"}
              </Text>
              <Button size="sm" onClick={toggleColorMode}>
                {colorMode === "light" ? "Dark" : "Light"}
              </Button>
            </Flex>

            <Separator />

            {/* Selector de sitio */}
            <Flex justifyContent="space-between" alignItems="center" gap={3}>
              <Text fontSize="sm" fontWeight="600" flexShrink={0}>
                Sitio
              </Text>
              <Select size="sm" value={selectedSite} onChange={e => switchSite(e.target.value)}>
                <option value="">Seleccionar sitio</option>
                {sites?.map(site => (
                  <option key={site.name} value={site.name}>{site.name}</option>
                ))}
              </Select>
            </Flex>

            <Separator />

            {/* Cerrar sesión */}
            <Button
              leftIcon={<Icon as={FiLogOut} />}
              colorScheme="red"
              variant="ghost"
              justifyContent="flex-start"
              size="sm"
              onClick={() => { window.location.href = '/#logout'; }}
              w="full"
            >
              Cerrar sesión
            </Button>

          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

Configurator.propTypes = {
  secondary: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  fixed: PropTypes.bool,
};
